export interface DayHours {
  open: string;
  close: string;
  closed: boolean;
}

export interface WeeklyHours {
  monday?: DayHours;
  tuesday?: DayHours;
  wednesday?: DayHours;
  thursday?: DayHours;
  friday?: DayHours;
  saturday?: DayHours;
  sunday?: DayHours;
}

export const DAYS_OF_WEEK = [
  'monday',
  'tuesday', 
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday'
] as const;

export const DAY_NAMES = {
  monday: 'service.details.day_names.monday',
  tuesday: 'service.details.day_names.tuesday',
  wednesday: 'service.details.day_names.wednesday', 
  thursday: 'service.details.day_names.thursday',
  friday: 'service.details.day_names.friday',
  saturday: 'service.details.day_names.saturday',
  sunday: 'service.details.day_names.sunday'
} as const;

// Helper function to convert time string to minutes since midnight
function timeToMinutes(timeString: string): number {
  if (!timeString) return 0;
  const [hours, minutes] = timeString.split(':').map(num => parseInt(num, 10));
  return hours * 60 + minutes;
}

// Format time in 12-hour format
export function formatTime12(time: string): string {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${minutes} ${ampm}`;
}

// Format time in 24-hour format
export function formatTime24(time: string): string {
  if (!time) return '';
  return time; // 24-hour format is already in HH:MM format
}

// Format time based on specified format
export function formatTime(time: string, timeFormat: '12' | '24' = '12'): string {
  return timeFormat === '24' ? formatTime24(time) : formatTime12(time);
}

// Parse opening hours string to WeeklyHours object
export function parseOpeningHours(hoursString: string | null): WeeklyHours | null {
  if (!hoursString) return null;
  
  try {
    // If it's already a JSON string, parse it
    if (typeof hoursString === 'string' && hoursString.trim().startsWith('{')) {
      return JSON.parse(hoursString) as WeeklyHours;
    }
    
    // If it's a simple string format, return null for now
    // This can be extended later to parse different string formats
    return null;
  } catch (error) {
    console.error('Error parsing opening hours:', error);
    return null;
  }
}

export function formatOpeningHours(
  openingHours: WeeklyHours | null, 
  timeFormat: '12' | '24' = '12',
  t?: (key: string) => string
): string {
  if (!openingHours) return '';
  
  const formattedDays: string[] = [];
  
  DAYS_OF_WEEK.forEach(day => {
    const dayHours = openingHours[day];
    if (dayHours) {
      const dayName = t ? t(DAY_NAMES[day]) : day.charAt(0).toUpperCase() + day.slice(1);
      if (dayHours.closed) {
        const closedText = t ? t('service.details.day_names.closed') : 'Closed';
        formattedDays.push(`${dayName}: ${closedText}`);
      } else {
        const openTime = formatTime(dayHours.open, timeFormat);
        const closeTime = formatTime(dayHours.close, timeFormat);
        formattedDays.push(`${dayName}: ${openTime} - ${closeTime}`);
      }
    }
  });
  
  return formattedDays.join('\n');
}

export function getCurrentDayKey(): keyof WeeklyHours {
  const now = new Date();
  const dayIndex = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
  const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return dayKeys[dayIndex] as keyof WeeklyHours;
}

export function isBusinessOpen(
  openingHours: WeeklyHours | null, 
  timeFormat: '12' | '24' = '12',
  t?: (key: string, options?: { time?: string }) => string
): { isOpen: boolean; status: string } {
  if (!openingHours) {
    const status = t ? t('service.details.opening_status.hours_unavailable') : 'Hours not available';
    return { isOpen: false, status };
  }
  
  const currentDay = getCurrentDayKey();
  const todayHours = openingHours[currentDay];
  
  if (!todayHours || todayHours.closed) {
    const status = t ? t('service.details.opening_status.closed_today') : 'Closed today';
    return { isOpen: false, status };
  }
  
  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const openMinutes = timeToMinutes(todayHours.open);
  const closeMinutes = timeToMinutes(todayHours.close);
  
  if (currentMinutes >= openMinutes && currentMinutes <= closeMinutes) {
    const closeTime = formatTime(todayHours.close, timeFormat);
    const status = t 
      ? t('service.details.opening_status.open_now_closes_at', { time: closeTime })
      : `Open now • Closes at ${closeTime}`;
    return { isOpen: true, status };
  } 
  
  if (currentMinutes < openMinutes) {
    const openTime = formatTime(todayHours.open, timeFormat);
    const status = t 
      ? t('service.details.opening_status.opens_at', { time: openTime })
      : `Opens at ${openTime}`;
    return { isOpen: false, status };
  }
  
  const status = t ? t('service.card.closed') : 'Closed';
  return { isOpen: false, status };
}

export function getNextOpeningTime(
  openingHours: WeeklyHours | null, 
  timeFormat: '12' | '24' = '12',
  t?: (key: string, options?: { day?: string; time?: string }) => string
): string {
  if (!openingHours) {
    return t ? t('service.details.opening_status.hours_unavailable') : 'Opening hours not available';
  }
  
  const currentDay = getCurrentDayKey();
  const currentDayIndex = DAYS_OF_WEEK.indexOf(currentDay as any);
  
  // Check remaining days this week
  for (let i = 1; i < 7; i++) {
    const nextDayIndex = (currentDayIndex + i) % 7;
    const nextDay = DAYS_OF_WEEK[nextDayIndex];
    const nextDayHours = openingHours[nextDay];
    
    if (nextDayHours && !nextDayHours.closed) {
      const dayName = t ? t(DAY_NAMES[nextDay]) : nextDay.charAt(0).toUpperCase() + nextDay.slice(1);
      const openTime = formatTime(nextDayHours.open, timeFormat);
      return t 
        ? t('service.details.opening_status.opens_next', { day: dayName, time: openTime })
        : `Opens ${dayName} at ${openTime}`;
    }
  }
  
  return t ? t('service.details.opening_status.hours_unavailable') : 'Opening hours not available';
}

// Helper function to create default opening hours
export function createDefaultOpeningHours(): WeeklyHours {
  const weekdayHours: DayHours = {
    open: '09:00',
    close: '17:00',
    closed: false
  };
  
  const weekendHours: DayHours = {
    open: '10:00',
    close: '16:00',
    closed: false
  };
  
  return {
    monday: weekdayHours,
    tuesday: weekdayHours,
    wednesday: weekdayHours,
    thursday: weekdayHours,
    friday: weekdayHours,
    saturday: weekendHours,
    sunday: { ...weekendHours, closed: true }
  };
}